import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, CircularProgress, TextField, Button,
  Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, 
  Divider, Pagination, InputAdornment, IconButton, Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import axios from 'axios';
import moment from 'moment';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import SearchIcon from '@mui/icons-material/Search';
import DiscountIcon from '@mui/icons-material/Discount';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

// Styled components for the table
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
  background: '#ffffff',
  marginBottom: theme.spacing(3)
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  color: '#1e293b',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
  whiteSpace: 'nowrap', // Prevent wrapping of text
}));

const PriceTableCell = styled(StyledTableCell)({
  cursor: 'pointer',
  textDecoration: 'underline',
  '&:hover': { 
    color: '#21ccbb',
    fontWeight: 500
  }
});

const StyledIcon = styled('span')({
  color: '#21ccbb',
  marginRight: '8px',
  verticalAlign: 'middle',
});

const StyledCircularProgress = styled(CircularProgress)({
  color: '#21ccbb',
});

// Styled components for the dialog
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.16)',
    background: '#ffffff',
    maxWidth: 500,
    width: '90%',
    margin: theme.spacing(2),
    overflow: 'hidden',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(90deg, #21ccbb 0%, #16a085 100%)',
  color: '#ffffff',
  fontFamily: "'Inter', sans-serif",
  fontWeight: 600,
  padding: theme.spacing(2),
  textAlign: 'center',
  position: 'relative',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  background: '#f8fafc',
  fontFamily: "'Inter', sans-serif",
}));

const StyledListItem = styled(ListItem)(({ theme, isTotal }) => ({
  borderRadius: 8,
  margin: theme.spacing(0.5, 0),
  padding: theme.spacing(1.5),
  background: isTotal ? 'rgba(33, 204, 187, 0.1)' : '#ffffff',
  boxShadow: isTotal ? 'none' : '0 2px 8px rgba(15, 23, 42, 0.05)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  display: 'flex', // Ensure content stays in a single row
  alignItems: 'center',
}));

const StyledListItemIcon = styled('span')({
  color: '#21ccbb',
  marginRight: 12,
  display: 'flex',
  alignItems: 'center',
});

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  background: 'rgba(15, 23, 42, 0.1)',
}));

// Custom debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const CommandesSection = ({ restaurantId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(moment().startOf('day'));
  const [endDate, setEndDate] = useState(moment().endOf('day'));
  const [openBreakdown, setOpenBreakdown] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  // Fetch orders function
  const fetchOrders = useCallback(async (search = searchTerm) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Veuillez vous connecter pour accéder aux commandes');
        setLoading(false);
        return;
      }

      const response = await axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/orders`, {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          page: pagination.page,
          limit: pagination.limit,
          search
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setOrders(response.data.orders);
      setPagination({
        ...pagination,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      });
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Accès refusé. Vérifiez vos permissions ou contactez l\'administrateur.');
      } else if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (err.response?.status === 404) {
        setError('Restaurant introuvable. Vérifiez l\'ID du restaurant.');
      } else {
        setError(`Erreur lors du chargement des commandes: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [restaurantId, startDate, endDate, pagination.page, pagination.limit]);

  // Debounced search function
  const debouncedFetchOrders = useCallback(debounce((search) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchOrders(search);
  }, 500), [fetchOrders]);

  // Effect for initial fetch and dependency changes
  useEffect(() => {
    fetchOrders();
  }, [restaurantId, startDate, endDate, pagination.page, fetchOrders]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    debouncedFetchOrders(newSearchTerm);
  };

  const handleOpenBreakdown = (order) => {
    setSelectedOrder(order);
    setOpenBreakdown(true);
  };

  const handleCloseBreakdown = () => {
    setOpenBreakdown(false);
    setSelectedOrder(null);
  };

  const handlePageChange = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const statusLabels = {
    0: 'En attente',
    1: 'En préparation',
    2: 'Livrée',
    3: 'Annulée',
    4: 'En cours de livraison',
  };

  const pointLabels = {
    0: 'Point A',
    1: 'Point B',
    2: 'Point C',
    'N/A': 'Non spécifié',
    'Request': 'Request',
  };

  const paiementLabels = {
    0: 'Espèces',
    1: 'Carte bancaire',
  };

  // Calculate total for display in PriceTableCell and Dialog
  const calculateDisplayTotal = (order) => {
    if (order.source === 'commandes') {
      const prixTotal = Number(order.prix_total) || 0;
      const fraisDeLivraison = Number(order.frais_de_livraison) || 0;
      const service = Number(order.service) || 0;
      const reduction = Number(order.reduction) || 0;
      return (prixTotal + fraisDeLivraison + service + reduction).toFixed(2);
    }
    return order.total.toFixed(2);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <StyledCircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2, color: '#dc2626' }}>
        <Typography>{error}</Typography>
        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button onClick={() => fetchOrders()} variant="contained">
            Réessayer
          </Button>
          {error.includes('connecter') && (
            <Button href="/login" variant="outlined">
              Se connecter
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ fontFamily: "'Inter', sans-serif" }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1e293b' }}>
          Gestion des Commandes
        </Typography>

        {/* Search and Date Range Picker */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <TextField
            sx={{ width: '40%' }}
            size="small"
            placeholder="Rechercher par ID, nom, téléphone ou livreur..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Date de début"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="Date de fin"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>
        </Box>

        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell><StyledIcon><PersonIcon fontSize="small" /></StyledIcon>ID</StyledTableCell>
                <StyledTableCell><StyledIcon><PersonIcon fontSize="small" /></StyledIcon>Client</StyledTableCell>
                <StyledTableCell><StyledIcon><CalendarTodayIcon fontSize="small" /></StyledIcon>Date</StyledTableCell>
                <StyledTableCell><StyledIcon><AttachMoneyIcon fontSize="small" /></StyledIcon>Total</StyledTableCell>
                <StyledTableCell><StyledIcon><AssignmentTurnedInIcon fontSize="small" /></StyledIcon>Statut</StyledTableCell>
                <StyledTableCell><StyledIcon><DeliveryDiningIcon fontSize="small" /></StyledIcon>Livreur</StyledTableCell>
                <StyledTableCell><StyledIcon><LocationOnIcon fontSize="small" /></StyledIcon>Point</StyledTableCell>
                <StyledTableCell><StyledIcon><PaymentIcon fontSize="small" /></StyledIcon>Paiement</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <StyledTableCell>{order.id}</StyledTableCell>
                  <StyledTableCell>{order.customerName}</StyledTableCell>
                  <StyledTableCell>
                    {moment(order.createdAt).format('DD/MM/YYYY HH:mm')}
                  </StyledTableCell>
                  <PriceTableCell onClick={() => handleOpenBreakdown(order)}>
                    {calculateDisplayTotal(order)} MAD
                  </PriceTableCell>
                  <StyledTableCell>{statusLabels[order.status]}</StyledTableCell>
                  <StyledTableCell>{order.livreur}</StyledTableCell>
                  <StyledTableCell>{pointLabels[order.point] || order.point}</StyledTableCell>
                  <StyledTableCell>{paiementLabels[order.moyenPaiement]}</StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {orders.length === 0 ? (
          <Box sx={{ mt: 3, textAlign: 'center', color: '#64748b' }}>
            <Typography>Aucune commande trouvée</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Price Breakdown Dialog */}
        <StyledDialog
          open={openBreakdown}
          onClose={handleCloseBreakdown}
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 300 }}
        >
          <StyledDialogTitle>Détails du montant</StyledDialogTitle>
          <StyledDialogContent>
            {selectedOrder && (
              <List>
                <StyledListItem>
                  <StyledListItemIcon>
                    <MonetizationOnIcon fontSize="small" />
                  </StyledListItemIcon>
                  <ListItemText 
                    primary="Sous-total" 
                    secondary={`${selectedOrder.total.toFixed(2)} MAD`} 
                    primaryTypographyProps={{ fontWeight: 500, color: '#1e293b' }}
                    secondaryTypographyProps={{ color: '#64748b', whiteSpace: 'nowrap' }}
                  />
                </StyledListItem>
                
                {selectedOrder.source === 'commandes' ? (
                  <>
                    <StyledDivider />
                    <StyledListItem>
                      <StyledListItemIcon>
                        <DeliveryDiningIcon fontSize="small" />
                      </StyledListItemIcon>
                      <ListItemText 
                        primary="Frais de livraison" 
                        secondary={`+ ${selectedOrder.frais_de_livraison.toFixed(2)} MAD`} 
                        primaryTypographyProps={{ fontWeight: 500, color: '#1e293b' }}
                        secondaryTypographyProps={{ color: '#64748b', whiteSpace: 'nowrap' }}
                      />
                    </StyledListItem>
                    <StyledListItem>
                      <StyledListItemIcon>
                        <RoomServiceIcon fontSize="small" />
                      </StyledListItemIcon>
                      <ListItemText 
                        primary="Service" 
                        secondary={`+ ${selectedOrder.service.toFixed(2)} MAD`} 
                        primaryTypographyProps={{ fontWeight: 500, color: '#1e293b' }}
                        secondaryTypographyProps={{ color: '#64748b', whiteSpace: 'nowrap' }}
                      />
                    </StyledListItem>
                    <StyledListItem>
                      <StyledListItemIcon>
                        <DiscountIcon fontSize="small" />
                      </StyledListItemIcon>
                      <ListItemText 
                        primary="Réduction" 
                        secondary={`${selectedOrder.reduction.toFixed(2)} MAD`} 
                        primaryTypographyProps={{ fontWeight: 500, color: '#1e293b' }}
                        secondaryTypographyProps={{ color: '#dc2626', whiteSpace: 'nowrap' }}
                      />
                    </StyledListItem>
                  </>
                ) : (
                  <>
                    <StyledDivider />
                    <StyledListItem>
                      <StyledListItemIcon>
                        <DeliveryDiningIcon fontSize="small" />
                      </StyledListItemIcon>
                      <ListItemText 
                        primary="Frais de livraison" 
                        secondary={`+ ${selectedOrder.frais_livraison.toFixed(2)} MAD`} 
                        primaryTypographyProps={{ fontWeight: 500, color: '#1e293b' }}
                        secondaryTypographyProps={{ color: '#64748b', whiteSpace: 'nowrap' }}
                      />
                    </StyledListItem>
                  </>
                )}
                
                <StyledDivider />
                <StyledListItem isTotal>
                  <StyledListItemIcon>
                    <AttachMoneyIcon fontSize="small" />
                  </StyledListItemIcon>
                  <ListItemText 
                    primary="Total" 
                    secondary={`${calculateDisplayTotal(selectedOrder)} MAD`} 
                    primaryTypographyProps={{ fontWeight: 700, color: '#1e293b' }}
                    secondaryTypographyProps={{ fontWeight: 600, color: '#21ccbb', whiteSpace: 'nowrap' }}
                  />
                </StyledListItem>
              </List>
            )}
          </StyledDialogContent>
        </StyledDialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CommandesSection;